import { NavLink } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { APP_NAME, ROUTE_NAMES } from '@tsgecart/config';
import { cn } from '@tsgecart/ui';
import { ADMIN_NAV_ITEMS } from '../nav/admin-nav-items.js';
import { useAppUser } from '../../../lib/auth/use-app-user.js';

export interface AdminSidebarProps {
  /** Collapsed = icon-only rail (desktop only); irrelevant on the mobile drawer, which is always full-width. */
  collapsed?: boolean;
  onNavigate?: () => void;
}

/**
 * Shared between the desktop fixed sidebar and the mobile drawer (see
 * admin-mobile-navigation.tsx) so the nav list itself is never duplicated.
 * Every entry is filtered by `data.roles` (backend-resolved, never Clerk
 * metadata -- Part 1/Part 15) before it ever renders; a `future` entry
 * still renders but is non-interactive with a "Soon" marker rather than
 * being a fake working page.
 */
export function AdminSidebar({ collapsed, onNavigate }: AdminSidebarProps) {
  const { data } = useAppUser();
  const roles = data?.roles ?? [];

  const visibleItems = ADMIN_NAV_ITEMS.filter((item) => item.anyRole.some((role) => roles.includes(role)));

  return (
    <nav aria-label="Admin navigation" className="flex h-full flex-col gap-1 px-3 py-4">
      <NavLink
        to={ROUTE_NAMES.adminDashboard}
        className="mb-4 flex items-center gap-2 px-2 text-lg font-bold text-gray-900"
      >
        <span className="bg-brand flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold">
          {APP_NAME.charAt(0)}
        </span>
        {!collapsed && <span className="truncate">{APP_NAME} Admin</span>}
      </NavLink>

      <ul className="flex flex-1 flex-col gap-1">
        {visibleItems.map((item) => (
          <li key={item.label}>
            {item.future || !item.route ? (
              <div
                aria-disabled="true"
                title={`${item.label} — coming in a future admin milestone`}
                className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm text-gray-400"
              >
                <item.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                {!collapsed && (
                  <>
                    <span className="truncate">{item.label}</span>
                    <span className="ml-auto flex items-center gap-1 text-xs">
                      <Lock className="h-3 w-3" aria-hidden="true" />
                      Soon
                    </span>
                  </>
                )}
              </div>
            ) : (
              <NavLink
                to={item.route}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cn(
                    'focus-visible:ring-brand flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium focus-visible:outline-none focus-visible:ring-2',
                    isActive
                      ? 'bg-brand/15 text-gray-900'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                  )
                }
              >
                <item.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                {!collapsed && <span className="truncate">{item.label}</span>}
              </NavLink>
            )}
          </li>
        ))}
      </ul>

      {!collapsed && (
        <NavLink
          to={ROUTE_NAMES.home}
          className="focus-visible:ring-brand mt-auto rounded-xl px-3 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2"
        >
          ← Return to store
        </NavLink>
      )}
    </nav>
  );
}
